import cn from "classnames";
import { forwardRef, ReactNode } from "react";
import { useViewMode } from "../../hooks";
import { TrackProps } from "./types";
import { getIsExtraSlideTemporaryActive } from "./utils";
import styles from "./styles.module.scss";

export const Track = forwardRef<HTMLUListElement, TrackProps>(
  (
    {
      activeSlideIndex,
      isCenterMode,
      gap,
      offset,
      slides,
      viewMode,
      dataTestId,
      transitionDuration,
      shouldRenderExtraSlides,
      extraSlidesMultiplier,
      setSlideRef,
      onTransitionEnd,
    },
    forwardedRef
  ) => {
    const { isSingleViewMode } = useViewMode(viewMode);

    const renderExtraSlides = (keyPrefix: string) => {
      if (!shouldRenderExtraSlides) {
        return null;
      }

      const extraSlides: ReactNode[] = [];

      for (let i = 0; i < extraSlidesMultiplier; i++) {
        extraSlides.push(
          slides.map((slide, index) => (
            <li
              key={`${keyPrefix}-${i}-${index}`}
              style={{ transitionDuration: `${transitionDuration}ms` }}
              className={cn(styles.slide, {
                [styles.single]: isSingleViewMode,
                [styles.active]:
                  isCenterMode &&
                  getIsExtraSlideTemporaryActive({
                    activeSlideIndex,
                    currentExtraSlidesMultiplier: i,
                    extraSlidesMultiplier,
                    index,
                    slidesCount: slides.length,
                  }),
              })}
              data-test-id={`${dataTestId}-${keyPrefix}`}
            >
              {slide}
            </li>
          ))
        );
      }

      return extraSlides;
    };

    return (
      <ul
        ref={forwardedRef}
        className={cn(styles.track, { [styles.single]: isSingleViewMode })}
        style={{
          translate: `${offset}px`,
          gap: `${gap}px`,
          transitionDuration: `${transitionDuration}ms`,
        }}
        data-test-id={dataTestId}
        onTransitionEnd={onTransitionEnd}
      >
        {renderExtraSlides("slide-before")}
        {slides.map((slide, index) => (
          <li
            key={index}
            ref={(element) => setSlideRef(element, index)}
            style={{ transitionDuration: `${transitionDuration}ms` }}
            className={cn(styles.slide, {
              [styles.single]: isSingleViewMode,
              [styles.active]: activeSlideIndex === index && isCenterMode,
            })}
            data-test-id={`${dataTestId}-slide`}
          >
            {slide}
          </li>
        ))}
        {renderExtraSlides("slide-after")}
      </ul>
    );
  }
);

Track.displayName = "Track";
